import React, { useRef, useState } from 'react';
import * as http from '../../../../utils/http';
import * as translator from '../../../../utils/translator';
import './PhotoUploadModal.css';
import './UkVerificationTasks.css';
import { clientConfig } from '../../../../config';
import { DoseProofSwitchData } from './types';
import checkIcon from './icons/proof-check.svg';
import uploadIcon from './icons/proof-upload.svg';
import cameraIcon from './icons/proof-camera.svg';
import doneCheckIcon from './icons/proof-done-check.svg';
import spinnerIcon from './icons/proof-spinner.svg';
import switchInfoIcon from './icons/proof-switch-info.svg';
import fileIcon from './icons/proof-file.svg';
import editIcon from './icons/task-edit.svg';

const galleryIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-gallery.svg`;

// Same limits as the dose-proof-upload endpoint (AC-5).
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'];
const ACCEPT_ATTRIBUTE = `${ACCEPTED_TYPES.join(',')},.heic,.heif`;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

type UploadStatus = 'empty' | 'uploading' | 'uploaded' | 'error';
type UploadError = 'network' | 'type' | 'size';

const isMobile = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

// Some browsers hand HEIC/HEIF photos over without a MIME type, which the API would reject.
const withMimeType = (file: File): File => {
  if (file.type || !/\.(heic|heif)$/i.test(file.name)) return file;
  return new File([file], file.name, { type: /\.heif$/i.test(file.name) ? 'image/heif' : 'image/heic' });
};

// "Mounjaro® 2.5 mg" → "2.5 mg" for the switch button (Figma); the full title when no dose is found.
const getDoseLabel = (productTitle: string): string => {
  const doses = productTitle.match(/\d+(?:[.,]\d+)?\s?mg\b/gi);
  return doses ? doses[doses.length - 1] : productTitle;
};

interface Props {
  hashID: string;
  countryID: string;
  language: string;
  stepLabel: string;
  initialFileName: string | null;
  switchData: DoseProofSwitchData | null;
  onClose: () => void;
  onUploaded: (fileName: string) => void;
  onSubmitted: (fileType?: string) => void;
  onSwitched: () => void;
}

/** UK WL task flow (HL-12997): the proof of previous use task — upload, submit, or "I don't have proof". */
const DoseProofModal: React.FC<Props> = ({
  hashID,
  countryID,
  language,
  stepLabel,
  initialFileName,
  switchData,
  onClose,
  onUploaded,
  onSubmitted,
  onSwitched,
}) => {
  const t = (key: string) => translator.translate(language, 'my_account', `VerificationTasks/${key}`);
  const [visible, setVisible] = useState(true);
  const [status, setStatus] = useState<UploadStatus>(initialFileName ? 'uploaded' : 'empty');
  const [fileName, setFileName] = useState<string | null>(initialFileName);
  const [uploadingIsImage, setUploadingIsImage] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [lastFile, setLastFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [showNoProof, setShowNoProof] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const failUpload = (error: UploadError) => {
    setUploadError(error);
    setStatus('error');
  };

  const uploadFile = async (selected: File) => {
    const file = withMimeType(selected);
    setLastFile(file);
    setSubmitError(false);
    if (!ACCEPTED_TYPES.includes(file.type)) return failUpload('type');
    if (file.size > MAX_FILE_SIZE) return failUpload('size');

    setUploadError(null);
    setFileName(file.name);
    setUploadingIsImage(file.type.startsWith('image/'));
    setProgress(0);
    setStatus('uploading');

    const formData = new FormData();
    formData.append('file', file);
    try {
      const res: any = await http.postToServer(
        `${countryID}/account/orders/${hashID}/dose-proof-upload`,
        formData,
        [http.MIDDLEWARES.CUSTOMER_ID],
        {
          onUploadProgress: (event: ProgressEvent) => {
            if (event.total) setProgress(Math.round((event.loaded / event.total) * 100));
          },
        }
      );
      const uploadedName = res.data?.fileName || file.name;
      setFileName(uploadedName);
      setFileType(file.type);
      setStatus('uploaded');
      onUploaded(uploadedName);
    } catch (error: any) {
      const httpStatus = error?.response?.status;
      failUpload(httpStatus === 415 ? 'type' : httpStatus === 413 ? 'size' : 'network');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadFile(file);
  };

  const handleTryAgain = () => {
    // A dropped connection retries the same file; a rejected file needs a new one.
    if (uploadError === 'network' && lastFile) uploadFile(lastFile);
    else fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(false);
    try {
      await http.postToServer(`${countryID}/account/orders/${hashID}/submit-dose-proof`, {}, [http.MIDDLEWARES.CUSTOMER_ID]);
    } catch {
      setSubmitting(false);
      setSubmitError(true);
      return;
    }
    onSubmitted(fileType);
    handleClose();
  };

  const handleSwitch = async () => {
    setSwitching(true);
    setSwitchError(false);
    try {
      await http.postToServer(`${countryID}/account/orders/${hashID}/dose-proof-switch`, {}, [http.MIDDLEWARES.CUSTOMER_ID]);
    } catch {
      setSwitching(false);
      setSwitchError(true);
      return;
    }
    onSwitched();
    handleClose();
  };

  const errorText = uploadError === 'type'
    ? t('proof_error_type')
    : uploadError === 'size' ? t('proof_error_size') : t('proof_error_network');

  const renderUploadArea = () => {
    if (status === 'uploading' && uploadingIsImage) {
      return (
        <div className="vtp-file-card vtp-uploading-image">
          <img src={spinnerIcon} alt="" className="vtp-spinner" />
          <p className="vtp-uploading-text">{t('proof_uploading_photo')}</p>
          <div className="vtp-progress-track">
            <div className="vtp-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <p className="vtp-uploading-pct">{progress}%</p>
        </div>
      );
    }

    if (status === 'uploading') {
      return (
        <div className="vtp-file-card">
          <div className="vtp-file-row">
            <img src={fileIcon} alt="" className="vtp-file-icon" />
            <div className="vtp-file-info">
              <p className="vtp-file-name">{fileName}</p>
              <div className="vtp-progress-track vtp-progress-track-small">
                <div className="vtp-progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <p className="vtp-file-status">{t('proof_uploading_file').replace('{{percent}}', String(progress))}</p>
            </div>
          </div>
        </div>
      );
    }

    if (status === 'uploaded') {
      return (
        <div className="vtp-file-card">
          <div className="vtp-file-row">
            <div className="vtp-done-circle">
              <img src={doneCheckIcon} alt="" />
            </div>
            <div className="vtp-file-info">
              <p className="vtp-file-name">{fileName}</p>
              <p className="vtp-file-status">{t('proof_uploaded')}</p>
            </div>
          </div>
          <button className="vtp-outline-btn" onClick={() => fileInputRef.current?.click()}>
            <img src={editIcon} alt="" />
            <span>{t('proof_replace_btn')}</span>
          </button>
        </div>
      );
    }

    return (
      <>
        <button
          className={`vtp-upload-zone${status === 'error' ? ' vtp-upload-zone-error' : ''}`}
          onClick={() => fileInputRef.current?.click()}
        >
          <img src={uploadIcon} alt="" className="vtp-upload-icon" />
          <span className="vtp-upload-title">{t('proof_upload_zone')}</span>
          <span className="vtp-upload-formats">{t('proof_upload_formats')}</span>
        </button>
        {status === 'error' && <p className="vtp-error" role="alert">{errorText}</p>}
        <div className="vtp-source-buttons">
          {isMobile && (
            <button className="vtp-outline-btn" onClick={() => cameraInputRef.current?.click()}>
              <img src={cameraIcon} alt="" />
              <span>{t('proof_camera_btn')}</span>
            </button>
          )}
          <button className="vtp-outline-btn" onClick={() => fileInputRef.current?.click()}>
            <img src={galleryIcon} alt="" />
            <span>{t('proof_gallery_btn')}</span>
          </button>
        </div>
      </>
    );
  };

  const fill = (key: string, product: string) => t(key).replace('{{product}}', product);

  return (
    <div
      className={`pum-overlay${visible ? ' pum-overlay-visible' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !showNoProof) handleClose(); }}
    >
      <div className={`pum-sheet vt-sheet-wide${visible ? ' pum-sheet-visible' : ''}`}>
        <div className="pum-header vt-header">
          <button className="vt-mobile-back" onClick={handleClose}>{t('mobile_back')}</button>
          <div className="pum-breadcrumb">
            <span className="pum-step vt-step-label">{stepLabel}</span>
            <span className="pum-breadcrumb-sep"> / </span>
            <span className="pum-breadcrumb-section vt-section-label">{t('proof_section_label')}</span>
          </div>
          <button className="pum-close-btn" onClick={handleClose} aria-label={t('close_aria')}>✕</button>
        </div>

        <div className="pum-body">
          <div className="vtp-card">
            <div className="vtp-columns">
              <div className="vtp-left">
                <p className="vtp-title">{t('proof_section_label')}</p>
                <p className="vtp-intro">
                  {switchData ? fill('proof_intro', switchData.originalProductTitle) : t('proof_intro_generic')}
                </p>
                <ul className="vtp-accepted">
                  {['proof_accept_prescription', 'proof_accept_invoice', 'proof_accept_pen'].map((key) => (
                    <li key={key} className="vtp-accepted-item">
                      <img src={checkIcon} alt="" />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
                <p className="vtp-hint">{t('proof_visible_hint')}</p>
              </div>
              <div className="vtp-right">{renderUploadArea()}</div>
            </div>
            <div className="vtp-divider" />
            <p className="vtp-privacy">{t('proof_privacy')}</p>
          </div>

          {status === 'error' && (
            <button className="pum-submit-btn pum-submit-btn-active" onClick={handleTryAgain}>
              {t('proof_try_again_btn')}
            </button>
          )}
          {(status === 'uploading' || status === 'uploaded') && (
            <button
              className={`pum-submit-btn ${status === 'uploaded' && !submitting ? 'pum-submit-btn-active' : 'pum-submit-btn-disabled'}`}
              onClick={handleSubmit}
              disabled={status !== 'uploaded' || submitting}
            >
              {t('proof_submit_btn')}
            </button>
          )}
          {submitError && <p className="pum-submit-error" role="alert">{t('proof_submit_failed')}</p>}
          {switchData && (
            <button className="vtp-no-proof-btn" onClick={() => setShowNoProof(true)} disabled={status === 'uploading'}>
              {t('proof_no_proof_btn')}
            </button>
          )}
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept={ACCEPT_ATTRIBUTE} style={{ display: 'none' }} onChange={handleFileChange} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileChange} />

      {showNoProof && switchData && (
        <div className="vtp-np-overlay" onClick={(e) => { if (e.target === e.currentTarget && !switching) setShowNoProof(false); }}>
          <div className="vtp-np-sheet" role="dialog" aria-modal="true">
            <span className="vtp-np-handle" />
            <p className="vtp-np-title">{fill('no_proof_title', switchData.allowedProductTitle)}</p>
            <p className="vtp-np-body">{fill('no_proof_body', switchData.allowedProductTitle)}</p>
            <div className="vtp-np-compare">
              <div className="vtp-np-row vtp-np-row-old">
                <div className="vtp-np-row-text">
                  <span className="vtp-np-row-label">{t('no_proof_ordered_label')}</span>
                  <span className="vtp-np-row-product">{switchData.originalProductTitle}</span>
                </div>
                <span className="vtp-np-row-price">{switchData.originalPrice}</span>
              </div>
              <div className="vtp-np-row vtp-np-row-new">
                <div className="vtp-np-row-text">
                  <span className="vtp-np-row-label">{t('no_proof_new_label')}</span>
                  <span className="vtp-np-row-product">{switchData.allowedProductTitle}</span>
                </div>
                <span className="vtp-np-row-price">{switchData.allowedPrice}</span>
              </div>
            </div>
            <div className="vtp-np-note">
              <img src={switchInfoIcon} alt="" width={20} height={20} />
              <p>{t('no_proof_charge_note').replace('{{price}}', switchData.allowedPrice)}</p>
            </div>
            {switchError && <p className="vtp-error" role="alert">{t('no_proof_failed')}</p>}
            <button className="vtp-np-switch-btn" onClick={handleSwitch} disabled={switching}>
              {t('no_proof_switch_btn').replace('{{dose}}', getDoseLabel(switchData.allowedProductTitle))}
            </button>
            <button className="vtp-np-back-btn" onClick={() => setShowNoProof(false)} disabled={switching}>
              {t('no_proof_back_btn')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoseProofModal;
